'use client';

/**
 * ChargeMobileDelegate Component
 *
 * Generic headless processor that delegates payment to the native mobile app.
 * Used for payment methods that cannot or should not be processed on the web
 * when running inside a mobile app WebView.
 *
 * Flow:
 * 1. Send `on_pay` message to native app with payment method data
 * 2. Native app handles the payment (PayPal redirect, CC processing, etc.)
 * 3. Listen for `on_pay_completed` message from native app
 * 4. Call onSuccess when completed
 *
 * Matches old flow behavior from purchase_details_for_mobile_app.js:
 * - forMobileBuyBtnOnClickHandler sends { action: 'on_pay', params: { ...option, purchaseFees } }
 * - Native app processes the payment and sends back 'on_pay_completed'
 *
 * Used by componentSelector.tsx as a mobile override for:
 * - PayPal (popup-based flow doesn't work in WebViews)
 * - Credit Card on older app versions (before web CC processing was supported)
 */

import React, { useEffect } from 'react';
import { sendOnPay, messageToAppKeys } from '../../mobile/mobileMessaging';
import type { ChargeProcessorProps } from '../../types';

/** Safe JSON parse — returns parsed object or false on failure */
const tryParseJSONObject = (jsonString: string): Record<string, any> | false => {
  try {
    const o = JSON.parse(jsonString);
    if (o && typeof o === 'object') return o;
  } catch { /* ignore */ }
  return false;
};

/**
 * Props for ChargeMobileDelegate.
 * Extends ChargeProcessorProps with the payment method kind and human-readable name
 * needed for the `on_pay` message sent to the native app.
 */
export interface ChargeMobileDelegateProps extends ChargeProcessorProps {
  /** Payment method kind (e.g., 'cc', 'pp') — sent to native app in on_pay message */
  paymentKind: string;
  /** Human-readable payment method name (e.g., 'Credit or Debit Card', 'PayPal') */
  humanKind: string;
}

export const ChargeMobileDelegate: React.FC<ChargeMobileDelegateProps> = ({
  amount,
  paymentSourceId,
  purchaseType,
  context,
  paymentKind,
  humanKind,
  onStatusChange,
  onSuccess,
  startSignal = 0
}) => {
  useEffect(() => {
    if (!startSignal) return undefined;

    onStatusChange?.('processing', 'Processing payment...');

    // Build params matching old flow's forMobileBuyBtnOnClickHandler format:
    // { ...option, purchaseFees }
    const params: Record<string, any> = {
      kind: paymentKind,
      humanKind,
      context: context || {},
      id: paymentSourceId,
      purchaseFees: 0,
      amount,
      purchase_type: purchaseType
    };

    sendOnPay(params);

    // Listen for on_pay_completed response from native app
    const handleMessage = (e: MessageEvent) => {
      const parsedMessage = tryParseJSONObject(e.data);
      if (!parsedMessage) return;

      const action = parsedMessage[messageToAppKeys.action];

      if (action === messageToAppKeys.onPayCompeted) {
        const messageParams = parsedMessage[messageToAppKeys.params];
        onSuccess({
          success: true,
          okMessage: messageParams?.[messageToAppKeys.okMessage],
          cashbackAmount: messageParams?.[messageToAppKeys.cashback]
        });
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [startSignal]);

  return null;
};
